import React from 'react';
import moment from "moment";
import neoLogo from "../../../../../assets/images/Neo_Logo_A.svg";

// Importações dos domínios para mapeamentos corretos
import {
    RUNNING_INTENSITY_ZONES,
    RUNNING_SEGMENT_TYPES,
    getRunningLabel,
} from "../../domain/running/constants";

import { STRENGTH_EXERCISE_METHODS } from "../../domain/strength/constants";
import { WOD_FORMATS, WOD_SCORE_TYPES } from "../../domain/wod/constants";

/**
 * PrintableWorkout
 * Componente oculto na tela que renderiza o treino selecionado
 * para impressão no padrão A4.
 */
class PrintableWorkout extends React.Component {

    // Helper para obter zona de esforço da corrida
    getRunningZone(value) {
        return (RUNNING_INTENSITY_ZONES || []).find(zone => zone.value === value);
    }

    // Helper para formatar o alvo de corrida
    formatRunningTarget(segment, paceGroups, clubSessionActive) {
        if (!segment) return "";

        if (segment.targetType === "pace") {
            if (clubSessionActive && paceGroups && paceGroups.length > 0) {
                const groupPaces = paceGroups.map(group => {
                    const groupKey = group.id || group.value;
                    const customTarget = segment.paceGroupTargetsById?.[groupKey] || {};
                    
                    if (customTarget.targetPaceMin || customTarget.targetPaceMax) {
                        return `${group.label}: ${customTarget.targetPaceMin || "-"}-${customTarget.targetPaceMax || "-"}`;
                    }
                    
                    let defaultPace = "-";
                    if (segment.segmentType === "interval") {
                        defaultPace = group.intervalPace || "-";
                    } else if (segment.segmentType === "run" && (group.longRunPace || group.easyPace)) {
                        defaultPace = group.longRunPace || group.easyPace || "-";
                    } else if (segment.segmentType === "walk") {
                        defaultPace = "Caminhar";
                    } else {
                        defaultPace = group.easyPace || "-";
                    }
                    return `${group.label}: ${defaultPace}`;
                });
                return groupPaces.join(" | ");
            }

            if (segment.targetPaceMin || segment.targetPaceMax) {
                return `${segment.targetPaceMin || "-"} a ${segment.targetPaceMax || "-"}/km`;
            }
            return "";
        }

        if (segment.targetType === "heart_rate") return segment.heartRateZone ? `FC: ${segment.heartRateZone}` : "";
        if (segment.targetType === "power") return segment.powerZone ? `Potência: ${segment.powerZone}` : "";
        if (segment.targetType === "cadence") return segment.cadenceTarget ? `Cadência: ${segment.cadenceTarget} RPM` : "";
        if (segment.targetType === "effort") {
            const zone = this.getRunningZone(segment.effortZone);
            return zone ? `${zone.label} (${zone.desc})` : (segment.effortZone || "");
        }
        return "";
    }

    // Helper para formatar a duração do segmento de corrida
    formatRunningDuration(segment) {
        if (!segment) return "";
        const reps = Number(segment.reps || 1);
        const prefix = reps > 1 ? `${reps}x ` : "";

        if (segment.durationType === "time") return `${prefix}${segment.duration || "-"} min`;
        if (segment.durationType === "distance") return `${prefix}${segment.distance || "-"}${segment.distanceUnit || "km"}`;
        if (segment.durationType === "open") return `${prefix}Livre`;
        if (segment.durationType === "lap_button") return `${prefix}Botão Lap`;
        return `${prefix}${segment.duration || segment.distance || "Livre"}`;
    }

    // Natação A4
    renderSwimmingTable(sets) {
        const thStyle = { padding: "10px 12px", fontWeight: "600", fontSize: "13px", borderBottom: "2px solid #ddd" };
        return (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "10px" }}>
                <thead>
                    <tr style={{ backgroundColor: "#f8f9fa", color: "#333", textAlign: "left", borderBottom: "2px solid #ddd" }}>
                        <th style={{ ...thStyle, width: "8%" }}>Série</th>
                        <th style={{ ...thStyle, width: "30%" }}>Metragem / Estilo</th>
                        <th style={{ ...thStyle, width: "18%" }}>Intensidade</th>
                        <th style={{ ...thStyle, width: "14%" }}>Saída/Intrv.</th>
                        <th style={{ ...thStyle, width: "30%" }}>Materiais e Foco Técnico / Obs</th>
                    </tr>
                </thead>
                <tbody>
                    {(sets || []).map((set, sIdx) => (
                        <tr key={sIdx} style={{ borderBottom: "1px solid #eee", backgroundColor: sIdx % 2 === 0 ? "#fff" : "#fafafa" }}>
                            <td style={{ padding: "10px 12px", fontWeight: "bold" }}>{sIdx + 1}</td>
                            <td style={{ padding: "10px 12px", fontWeight: "600", color: "#111" }}>
                                {set.reps ? `${set.reps}x ` : ""}{set.distance ? `${set.distance}m ` : ""}{set.style || "Livre"}
                            </td>
                            <td style={{ padding: "10px 12px", color: "#e67e22", fontWeight: "600" }}>{set.intensity || ""}</td>
                            <td style={{ padding: "10px 12px" }}>{set.interval || ""}</td>
                            <td style={{ padding: "10px 12px", fontSize: "12px", color: "#555" }}>
                                {set.equipment && set.equipment !== "Sem material" && (
                                    <div style={{ marginBottom: "2px" }}><strong>Material:</strong> {set.equipment}</div>
                                )}
                                {set.technicalFocus && (
                                    <div style={{ marginBottom: "2px" }}><strong>Foco:</strong> {set.technicalFocus}</div>
                                )}
                                {set.description && <div style={{ fontStyle: "italic", color: "#666" }}>Obs: {set.description}</div>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    // Musculação A4
    renderStrengthTable(sets) {
        const thStyle = { padding: "10px 12px", fontWeight: "600", fontSize: "13px", borderBottom: "2px solid #ddd" };
        return (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "10px" }}>
                <thead>
                    <tr style={{ backgroundColor: "#f8f9fa", color: "#333", textAlign: "left", borderBottom: "2px solid #ddd" }}>
                        <th style={{ ...thStyle, width: "5%" }}>#</th>
                        <th style={{ ...thStyle, width: "30%" }}>Exercício / Método</th>
                        <th style={{ ...thStyle, width: "18%" }}>Séries x Reps</th>
                        <th style={{ ...thStyle, width: "10%" }}>Carga</th>
                        <th style={{ ...thStyle, width: "15%" }}>Cadência / Descanso</th>
                        <th style={{ ...thStyle, width: "22%" }}>Instruções e Obs</th>
                    </tr>
                </thead>
                <tbody>
                    {(sets || []).map((set, sIdx) => {
                        const methodObj = (STRENGTH_EXERCISE_METHODS || []).find(m => m.value === set.strengthMethod);
                        const methodName = methodObj ? methodObj.label : set.strengthMethod;
                        const isSpecialMethod = set.strengthMethod && set.strengthMethod !== "straight";

                        return (
                            <tr key={sIdx} style={{ borderBottom: "1px solid #eee", backgroundColor: sIdx % 2 === 0 ? "#fff" : "#fafafa" }}>
                                <td style={{ padding: "10px 12px", fontWeight: "bold" }}>{sIdx + 1}</td>
                                <td style={{ padding: "10px 12px" }}>
                                    <div style={{ fontWeight: "600", color: "#111" }}>{set.exercise || set.name || set.movement || "Exercício"}</div>
                                    {isSpecialMethod && (
                                        <div style={{ fontSize: "11px", fontWeight: "bold", color: "#d9534f", marginTop: "2px" }}>
                                            [Método: {methodName}]
                                        </div>
                                    )}
                                    {(set.muscleGroup || set.equipment) && (
                                        <div style={{ fontSize: "11px", color: "#777", marginTop: "2px" }}>
                                            {[set.muscleGroup && `Grupo: ${set.muscleGroup}`, set.equipment && `Equip: ${set.equipment}`].filter(Boolean).join(" | ")}
                                        </div>
                                    )}
                                </td>
                                <td style={{ padding: "10px 12px", fontWeight: "600", color: "#27ae60" }}>
                                    {[set.sets && `${set.sets} séries`, set.reps && `${set.reps} reps`].filter(Boolean).join(" x ")}
                                </td>
                                <td style={{ padding: "10px 12px", fontWeight: "700" }}>
                                    {set.load ? `${set.load} kg` : ""}
                                </td>
                                <td style={{ padding: "10px 12px", fontSize: "12px" }}>
                                    {set.tempo && <div>Cadência: {set.tempo}</div>}
                                    {set.rest && <div>Descanso: {set.rest}</div>}
                                </td>
                                <td style={{ padding: "10px 12px", fontSize: "12px", color: "#555" }}>
                                    {set.description && <div style={{ marginBottom: "2px", fontStyle: "italic" }}>Obs: {set.description}</div>}
                                    {set.instructions && (
                                        <div style={{ fontSize: "11px", borderLeft: "2px solid #777", paddingLeft: "4px", color: "#666" }}>
                                            {set.instructions}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }

    // Corrida A4
    renderRunningTable(sets, paceGroups, clubSessionActive) {
        const thStyle = { padding: "10px 12px", fontWeight: "600", fontSize: "13px", borderBottom: "2px solid #ddd" };
        return (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "10px" }}>
                <thead>
                    <tr style={{ backgroundColor: "#f8f9fa", color: "#333", textAlign: "left", borderBottom: "2px solid #ddd" }}>
                        <th style={{ ...thStyle, width: "5%" }}>#</th>
                        <th style={{ ...thStyle, width: "20%" }}>Volume / Estímulo</th>
                        <th style={{ ...thStyle, width: "40%" }}>Alvo de Intensidade / Paces</th>
                        <th style={{ ...thStyle, width: "15%" }}>Recuperação</th>
                        <th style={{ ...thStyle, width: "20%" }}>Observações</th>
                    </tr>
                </thead>
                <tbody>
                    {(sets || []).map((set, sIdx) => {
                        const segmentLabel = set.label || getRunningLabel(RUNNING_SEGMENT_TYPES, set.segmentType);
                        const durationText = this.formatRunningDuration(set);
                        const targetText = this.formatRunningTarget(set, paceGroups, clubSessionActive);

                        return (
                            <tr key={sIdx} style={{ borderBottom: "1px solid #eee", backgroundColor: sIdx % 2 === 0 ? "#fff" : "#fafafa" }}>
                                <td style={{ padding: "10px 12px", fontWeight: "bold" }}>{sIdx + 1}</td>
                                <td style={{ padding: "10px 12px" }}>
                                    <div style={{ fontWeight: "600", color: "#111" }}>{durationText}</div>
                                    {segmentLabel && <div style={{ fontSize: "11px", color: "#666" }}>Tipo: {segmentLabel}</div>}
                                    {set.terrain && set.terrain !== "flat" && (
                                        <div style={{ fontSize: "10px", color: "#888", fontStyle: "italic" }}>Terreno: {set.terrain}</div>
                                    )}
                                </td>
                                <td style={{ padding: "10px 12px", fontWeight: "500", color: "#2980b9" }}>
                                    {targetText}
                                </td>
                                <td style={{ padding: "10px 12px" }}>
                                    {set.restAfter || ""}
                                </td>
                                <td style={{ padding: "10px 12px", fontSize: "12px", color: "#555", fontStyle: "italic" }}>
                                    {set.notes || ""}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }

    // WOD A4
    renderWodTable(sets) {
        const thStyle = { padding: "10px 12px", fontWeight: "600", fontSize: "13px", borderBottom: "2px solid #ddd" };
        return (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "10px" }}>
                <thead>
                    <tr style={{ backgroundColor: "#f8f9fa", color: "#333", textAlign: "left", borderBottom: "2px solid #ddd" }}>
                        <th style={{ ...thStyle, width: "5%" }}>#</th>
                        <th style={{ ...thStyle, width: "30%" }}>Movimento / Exercício</th>
                        <th style={{ ...thStyle, width: "15%" }}>Metas (Reps/Dist/Cal)</th>
                        <th style={{ ...thStyle, width: "12%" }}>Carga</th>
                        <th style={{ ...thStyle, width: "23%" }}>Adaptações (Scaling)</th>
                        <th style={{ ...thStyle, width: "15%" }}>Notas</th>
                    </tr>
                </thead>
                <tbody>
                    {(sets || []).map((set, sIdx) => (
                        <tr key={sIdx} style={{ borderBottom: "1px solid #eee", backgroundColor: sIdx % 2 === 0 ? "#fff" : "#fafafa" }}>
                            <td style={{ padding: "10px 12px", fontWeight: "bold" }}>{sIdx + 1}</td>
                            <td style={{ padding: "10px 12px" }}>
                                <div style={{ fontWeight: "600", color: "#111" }}>{set.movement || set.exercise || set.name || "Movimento"}</div>
                                {set.category && <div style={{ fontSize: "10px", color: "#888" }}>Cat: {set.category}</div>}
                            </td>
                            <td style={{ padding: "10px 12px" }}>
                                {set.reps && <div style={{ fontWeight: "600" }}>Reps: {set.reps} {set.unit || ""}</div>}
                                {([set.distance, set.calories, set.time, set.height].some(Boolean)) && (
                                    <div style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}>
                                        {[
                                            set.distance && `Dist: ${set.distance}`,
                                            set.calories && `Cal: ${set.calories}`,
                                            set.time && `Tempo: ${set.time}`,
                                            set.height && `Alt: ${set.height}`
                                        ].filter(Boolean).join(" | ")}
                                    </div>
                                )}
                            </td>
                            <td style={{ padding: "10px 12px", fontWeight: "700" }}>
                                {set.load || ""}
                            </td>
                            <td style={{ padding: "10px 12px", fontSize: "11px", color: "#444" }}>
                                {set.scaling && (set.scaling.rx || set.scaling.intermediate || set.scaling.beginner) ? (
                                    <div>
                                        {set.scaling.rx && <div><strong>RX:</strong> {set.scaling.rx}</div>}
                                        {set.scaling.intermediate && <div><strong>Interm:</strong> {set.scaling.intermediate}</div>}
                                        {set.scaling.beginner && <div><strong>Inic:</strong> {set.scaling.beginner}</div>}
                                    </div>
                                ) : ""}
                            </td>
                            <td style={{ padding: "10px 12px", fontSize: "11px", color: "#666", fontStyle: "italic" }}>
                                {set.notes || ""}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    renderBlock(workoutType, block, workout, paceGroups, isClub) {
        const sets = block.sets;
        if (!sets || sets.length === 0) {
            return <div style={{ fontSize: "13px", fontStyle: "italic", color: "#888", padding: "15px 0", textAlign: "center" }}>Nenhum exercício neste bloco.</div>;
        }

        switch (workoutType) {
            case "swimming": return this.renderSwimmingTable(sets);
            case "strength": return this.renderStrengthTable(sets);
            case "running": return this.renderRunningTable(sets, paceGroups, isClub);
            case "wod": return this.renderWodTable(sets);
            default: return this.renderSwimmingTable(sets);
        }
    }

    render() {
        const { workout, tenantName, tenantLogo } = this.props;

        if (!workout) return null;

        const date = workout?.date
            ? moment(workout.date).format("DD/MM/YYYY")
            : "--";
            
        const workoutType = workout.workoutType || "strength";
        const clubSession = workout.clubSession || {};
        const isClub = !!clubSession.isClubSession;
        const paceGroups = (clubSession.paceGroups || []).filter(g => g.enabled !== false);
        const cyclePlan = workout.cyclePlan || {};
        const isCycleActive = !!cyclePlan.enabled;

        return (
            <div className="printable-workout-page" style={{ 
                padding: "40px", 
                fontFamily: "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif", 
                color: "#333", 
                backgroundColor: "#fff",
                minHeight: "297mm", // Padrão A4
                boxSizing: "border-box"
            }}>

                {/* ═══ CABEÇALHO A4 ═══ */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #333", paddingBottom: "15px", marginBottom: "15px" }}>
                    {/* ESQUERDA: Logo e Informações Básicas */}
                    <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                        {tenantLogo && (
                            <img src={tenantLogo} alt="Logo" style={{ maxHeight: "65px", maxWidth: "150px", objectFit: "contain" }} />
                        )}
                        <div>
                            <h2 style={{ margin: "0 0 2px 0", fontSize: "16px", fontWeight: "700", color: "#2c3e50", textTransform: "uppercase" }}>
                                {tenantName || "NeoApp"}
                            </h2>
                            <h1 style={{ margin: "0 0 2px 0", fontSize: "20px", fontWeight: "800", color: "#111" }}>
                                {workout?.title || workout?.name || "Treino"}
                            </h1>
                            <div style={{ fontSize: "12px", color: "#666", display: "flex", gap: "12px" }}>
                                <span><strong>Data:</strong> {date}</span>
                                <span>•</span>
                                <span><strong>Modalidade:</strong> {
                                    workoutType === "swimming" ? "Natação" :
                                    workoutType === "strength" ? "Musculação" :
                                    workoutType === "running" ? "Corrida" :
                                    workoutType === "wod" ? "WOD" : workoutType
                                }</span>
                            </div>
                        </div>
                    </div>

                    {/* DIREITA: Aluno & Professor */}
                    <div style={{ textAlign: "right", fontSize: "13px", color: "#444" }}>
                        {workout?.memberName && (
                            <div style={{ marginBottom: "2px" }}><strong>Aluno:</strong> {workout.memberName}</div>
                        )}
                        {workout?.teacherName && (
                            <div><strong>Professor:</strong> {workout.teacherName}</div>
                        )}
                    </div>
                </div>

                {/* ═══ DETALHES COMPACTOS DE CLUBE E CICLO (SUB-HEADER A4) ═══ */}
                {(isClub || isCycleActive) && (
                    <div style={{ 
                        fontSize: "12px", 
                        color: "#444", 
                        backgroundColor: "#f8f9fa", 
                        padding: "8px 12px", 
                        border: "1px solid #e9ecef", 
                        borderRadius: "4px",
                        marginBottom: "20px",
                        lineHeight: "1.5"
                    }}>
                        {isClub && (
                            <div style={{ marginBottom: isCycleActive ? "6px" : "0" }}>
                                <strong>Treino de Clube:</strong> {
                                    [
                                        clubSession.teamName && `Turma: ${clubSession.teamName}`,
                                        clubSession.coachName && `Prof: ${clubSession.coachName}`,
                                        clubSession.startTime && `Horário: ${clubSession.startTime}`,
                                        clubSession.meetingPoint && `Ponto de Encontro: ${clubSession.meetingPoint}`,
                                        clubSession.routeName && `Rota: ${clubSession.routeName}`,
                                        clubSession.safetyNotes && `Segurança: ${clubSession.safetyNotes}`
                                    ].filter(Boolean).join(" | ")
                                }
                            </div>
                        )}
                        {isClub && paceGroups.length > 0 && (
                            <div style={{ fontSize: "11px", color: "#666", marginBottom: isCycleActive ? "6px" : "0", borderTop: "1px dotted #ccc", paddingTop: "6px", marginTop: "6px" }}>
                                <strong>Paces Referência:</strong> {
                                    paceGroups.map(group => {
                                        const paces = [
                                            group.easyPace && `Leve ${group.easyPace}`,
                                            group.thresholdPace && `Limiar ${group.thresholdPace}`,
                                            group.intervalPace && `Tiros ${group.intervalPace}`,
                                            group.longRunPace && `Longão ${group.longRunPace}`
                                        ].filter(Boolean).join(" • ");
                                        return `${group.label}: [${paces}]`;
                                    }).join(" | ")
                                }
                            </div>
                        )}
                        {isCycleActive && (
                            <div style={{ borderTop: isClub ? "1px dotted #ccc" : "none", paddingTop: isClub ? "6px" : "0", marginTop: isClub ? "6px" : "0" }}>
                                <strong>Planejamento do Ciclo:</strong> {
                                    [
                                        cyclePlan.raceName && `Prova Meta: ${cyclePlan.raceName}`,
                                        cyclePlan.raceDistance && `Distância: ${cyclePlan.raceDistance}`,
                                        cyclePlan.raceDate && `Data: ${moment(cyclePlan.raceDate).format("DD/MM/YYYY")}`,
                                        cyclePlan.phase && `Fase: ${cyclePlan.phase.toUpperCase()}`,
                                        cyclePlan.weekNumber && `Semana: ${cyclePlan.weekNumber}${cyclePlan.totalWeeks ? ` de ${cyclePlan.totalWeeks}` : ""}`,
                                        cyclePlan.plannedWeeklyKm && `Volume: ${cyclePlan.plannedWeeklyKm} km/sem`
                                    ].filter(Boolean).join(" | ")
                                }
                            </div>
                        )}
                    </div>
                )}

                {/* ═══ DESCRIÇÃO DO TREINO ═══ */}
                {workout?.description && (
                    <div style={{ marginBottom: "25px", padding: "12px 16px", backgroundColor: "#f8f9fa", borderRadius: "6px", border: "1px solid #e9ecef" }}>
                        <h4 style={{ margin: "0 0 6px 0", fontSize: "14px", fontWeight: "700", color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>Objetivos / Instruções Gerais</h4>
                        <p style={{ margin: 0, fontSize: "13px", color: "#555", whiteSpace: "pre-wrap", lineHeight: "1.5" }}>
                            {workout.description}
                        </p>
                    </div>
                )}

                {/* ═══ BLOCOS DE TREINO ═══ */}
                {(workout.blocks || []).map((block, bIndex) => {
                    const isWodBlock = workoutType === "wod";
                    const hasWodSpecs = isWodBlock && (block.format || block.timeCap || block.stimulus || block.scalingNotes);

                    return (
                        <div key={bIndex} style={{ marginBottom: "25px", pageBreakInside: "avoid" }}>
                            <div style={{ 
                                display: "flex", 
                                justifyContent: "space-between", 
                                alignItems: "center", 
                                paddingBottom: "6px", 
                                borderBottom: "2px solid #2c3e50",
                                marginBottom: "8px"
                            }}>
                                <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "800", color: "#2c3e50", textTransform: "uppercase" }}>
                                    {block.name || `Bloco ${bIndex + 1}`}
                                </h3>

                                {/* Exibe se tiver repetição no bloco de corrida */}
                                {workoutType === "running" && block.repeat && block.repeat > 1 && (
                                    <span style={{ fontSize: "12px", fontWeight: "bold", backgroundColor: "#eef2ff", color: "#3b5de7", padding: "2px 8px", borderRadius: "4px" }}>
                                        REPETIR {block.repeat}x {block.restBetweenRepeats ? `(Descanso: ${block.restBetweenRepeats})` : ""}
                                    </span>
                                )}
                            </div>

                            {/* WOD Specs no Cabeçalho de Bloco */}
                            {hasWodSpecs && (
                                <div style={{ 
                                    display: "grid", 
                                    gridTemplateColumns: "1fr 1fr 1fr", 
                                    gap: "10px",
                                    border: "1px solid #ddd", 
                                    borderRadius: "4px", 
                                    padding: "10px 12px", 
                                    marginBottom: "10px", 
                                    backgroundColor: "#fafafa", 
                                    fontSize: "12px"
                                }}>
                                    {block.format && (
                                        <div>
                                            <strong>Formato:</strong> {
                                                (WOD_FORMATS || []).find(f => f.value === block.format)?.label?.toUpperCase() || block.format.toUpperCase()
                                            }
                                        </div>
                                    )}
                                    {block.timeCap && <div><strong>Time Cap:</strong> {block.timeCap}</div>}
                                    {block.scoreType && (
                                        <div>
                                            <strong>Placar:</strong> {
                                                (WOD_SCORE_TYPES || []).find(s => s.value === block.scoreType)?.label || block.scoreType
                                            }
                                        </div>
                                    )}
                                    {block.stimulus && <div style={{ gridColumn: "1 / -1", borderTop: "1px solid #eee", paddingTop: "4px", marginTop: "2px" }}><strong>Estímulo de Intensidade:</strong> {block.stimulus}</div>}
                                    {block.scalingNotes && <div style={{ gridColumn: "1 / -1", borderTop: "1px dotted #ccc", paddingTop: "4px", color: "#555" }}><strong>Adaptações (Scaling):</strong> {block.scalingNotes}</div>}
                                </div>
                            )}

                            {block.description && (
                                <p style={{ margin: "0 0 10px 0", fontSize: "12px", fontStyle: "italic", color: "#666" }}>
                                    {block.description}
                                </p>
                            )}
                            
                            {/* Renderizador de tabelas customizadas por modalidade */}
                            {this.renderBlock(workoutType, block, workout, paceGroups, isClub)}
                        </div>
                    );
                })}

                {/* ═══ RODAPÉ A4 ═══ */}
                <div style={{ marginTop: "50px", paddingTop: "15px", borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "11px", color: "#999", fontStyle: "italic" }}>
                        Gerado em {moment().format("DD/MM/YYYY [às] HH:mm")}.
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ fontSize: "11px", fontWeight: "700", color: "#aaa", letterSpacing: "1px" }}>POWERED BY</span>
                        <img src={neoLogo} alt="NeoApp" style={{ height: "14px", filter: "grayscale(100%) opacity(0.6)" }} />
                    </div>
                </div>
            </div>
        );
    }
}

export default PrintableWorkout;
